import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, Truck, Factory, ClipboardCheck, ChevronRight, ArrowRight } from "lucide-react";
import { fmtEUR } from "@/lib/format";
import { KpiCard, Widget, Priorites, Ligne, libPeriode } from "@/components/dashboards/commun";
import PanneauAdministratif from "@/components/PanneauAdministratif";

const CRIT_COULEUR = { critique: "#B91C1C", elevee: "#B45309", normale: "#1D4ED8" };
const CRIT_BG = { critique: "#FEE2E2", elevee: "#FEF3C7", normale: "#EFF6FF" };

const STATUT_COULEUR = {
  a_controler: "#6B7280", incomplet: "#B45309", conforme: "#16A34A",
  en_commande: "#1D4ED8", en_preparation: "#7C3AED", pret_a_livrer: "#0891B2",
  livre_a_facturer: "#EA580C", facture_a_encaisser: "#DB2777", solde: "#111827",
};

const STATUT_OEM_LABEL = {
  transmise: "Transmise", acceptee: "Acceptée", en_production: "En production",
  produite: "Produite", en_transit: "En transit",
};

export const DashSecretaire = ({ d }) => {
  const navigate = useNavigate();
  const [dossierOuvert, setDossierOuvert] = useState(null);
  const k = d.kpis || {};
  const w = d.widgets || {};
  const totalStatuts = (w.repartition_statuts || []).reduce((s, x) => s + x.nb, 0);

  return (
    <div data-testid="dash-secretaire">
      {w.priorites && <Priorites items={w.priorites} />}

      {/* KPI — vue "file de travail" (pas de KPI financier au premier plan) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.dossiers_bloques && (
          <KpiCard
            icon={AlertTriangle}
            couleur={CRIT_COULEUR[k.dossiers_bloques.criticite] || "#B91C1C"}
            titre="Dossiers bloqués"
            valeur={k.dossiers_bloques.valeur}
            testId="kpi-dossiers-bloques"
            onClick={() => navigate("/dossiers-administratifs?vue=a_traiter")}
            sub={<span className="text-[11px]" style={{ color: CRIT_COULEUR[k.dossiers_bloques.criticite] }}>{k.dossiers_bloques.detail}</span>}
          />
        )}
        {k.delai_admin_moyen && (
          <KpiCard
            icon={Clock}
            couleur="#7C3AED"
            titre="Délai admin. moyen"
            valeur={`${k.delai_admin_moyen.valeur} ${k.delai_admin_moyen.unite}`}
            testId="kpi-delai-admin"
            sub={<span className="text-[11px] text-[--idc-muted]">{k.delai_admin_moyen.libelle}</span>}
          />
        )}
        {k.livres_non_factures && (
          <KpiCard
            icon={Truck}
            couleur="#EA580C"
            titre="Livrés non facturés"
            valeur={k.livres_non_factures.valeur}
            testId="kpi-livres-nonfact"
            onClick={() => navigate("/dossiers-administratifs?vue=tous")}
            sub={<span className="text-[11px] text-[--idc-muted]">
              {fmtEUR(k.livres_non_factures.montant || 0)}
              {k.livres_non_factures.plus_ancien_j > 0 && ` · ${k.livres_non_factures.plus_ancien_j}j`}
            </span>}
          />
        )}
        {k.reste_a_encaisser && (
          <KpiCard
            icon={ClipboardCheck}
            couleur="#DB2777"
            titre="Reste à encaisser"
            valeur={fmtEUR(k.reste_a_encaisser.valeur)}
            testId="kpi-reste-encaisser"
            sub={<span className="text-[11px]" style={{ color: k.reste_a_encaisser.echu > 0 ? "#B91C1C" : "var(--idc-muted)" }}>
              {k.reste_a_encaisser.echu > 0 ? `${fmtEUR(k.reste_a_encaisser.echu)} échu` : "0 € échu"}
            </span>}
          />
        )}
      </div>

      {/* Répartition par statut + blocages par responsable */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="idc-card p-4 lg:col-span-2" data-testid="widget-repartition-statuts">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[13px] font-semibold">Répartition par statut ({totalStatuts})</span>
            <button onClick={() => navigate("/dossiers-administratifs?vue=tous")}
              className="text-[11px] font-semibold text-[#2F6FED] hover:underline flex items-center gap-1"
              data-testid="voir-tous-btn">
              Voir tous <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {totalStatuts === 0 ? (
            <p className="text-[12px] text-[--idc-muted]">Aucun dossier en cours.</p>
          ) : (
            <div className="space-y-1.5">
              {(w.repartition_statuts || []).map((r) => {
                const pct = totalStatuts ? Math.round(r.nb / totalStatuts * 100) : 0;
                const col = STATUT_COULEUR[r.code] || "#64748B";
                return (
                  <div key={r.code}
                    className="flex items-center gap-3 cursor-pointer hover:bg-[#F8F9FB] px-2 py-1 rounded"
                    onClick={() => navigate(`/dossiers-administratifs?vue=tous&statut=${r.code}`)}
                    data-testid={`stat-${r.code}`}>
                    <span className="text-[12px] text-[--idc-text] w-[130px] shrink-0">{r.label}</span>
                    <div className="flex-1 h-2 bg-[#EEF1F6] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: col }} />
                    </div>
                    <span className="text-[12px] font-semibold text-[--idc-text] w-6 text-right">{r.nb}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="idc-card p-4" data-testid="widget-blocages-responsable">
          <div className="text-[13px] font-semibold mb-3">Blocages par responsable</div>
          {(w.blocages_par_responsable || []).length === 0 ? (
            <p className="text-[12px] text-[--idc-muted]">Aucun blocage. 🎉</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(w.blocages_par_responsable || []).map((r) => (
                <button key={r.code}
                  onClick={() => navigate(`/dossiers-administratifs?vue=tous&responsable=${r.code}`)}
                  className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity"
                  style={{
                    background: r.code === "secretaire" ? "#FEE2E2" : "#EEF1F6",
                    color: r.code === "secretaire" ? "#B91C1C" : "#374151",
                  }}
                  data-testid={`chip-resp-${r.code}`}>
                  {r.label} <span className="opacity-70">{r.nb}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ma file du jour + livraisons semaine + commandes usine */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Widget titre="Ma file du jour" icon={AlertTriangle} testId="widget-file-travail"
          vide={(w.file_travail || []).length === 0 && "Aucun dossier bloqué. 🎉"}>
          {(w.file_travail || []).map((f) => {
            const b = f.blocage || {};
            const col = CRIT_COULEUR[b.criticite] || "#64748B";
            const bg = CRIT_BG[b.criticite] || "#F3F4F6";
            return (
              <Ligne key={f.id} onClick={() => setDossierOuvert(f.id)} testId={`file-${f.id}`}>
                <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 uppercase shrink-0"
                  style={{ background: bg, color: col }}>{b.criticite || ""}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{f.numero} — {f.client}</div>
                  <div className="text-[11px] text-[--idc-muted] truncate">{b.libelle} · resp. {b.responsable}</div>
                </div>
                <span className="text-[10px] text-[--idc-muted] uppercase shrink-0">{f.perimetre}</span>
              </Ligne>
            );
          })}
        </Widget>

        <Widget titre="Livraisons de la semaine" icon={Truck} testId="widget-livraisons-semaine"
          vide={(w.livraisons_semaine || []).length === 0 && "Aucune livraison prévue cette semaine."}>
          {(w.livraisons_semaine || []).map((l) => (
            <Ligne key={l.id} onClick={() => setDossierOuvert(l.id)} testId={`liv-sem-${l.id}`}>
              <span className="font-mono text-[12px] text-[--idc-muted] shrink-0">{l.date_prevue}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{l.numero} — {l.client}</div>
                <div className="text-[11px] text-[--idc-muted] truncate">
                  {l.manquantes.length === 0 ? "✓ Toutes conditions OK" : `${l.manquantes.length} condition(s) manquante(s)`}
                </div>
              </div>
              {l.manquantes.length > 0 && (
                <span className="text-[10.5px] font-bold rounded-full px-2 py-0.5 shrink-0"
                  style={{ background: "#FEF3C7", color: "#B45309" }}>À finaliser</span>
              )}
            </Ligne>
          ))}
        </Widget>

        <Widget titre="Commandes usine à surveiller" icon={Factory} testId="widget-commandes-usine"
          vide={(w.commandes_usine || []).length === 0 && "Aucune commande usine active."}>
          {(w.commandes_usine || []).map((c) => (
            <Ligne key={c.id} onClick={() => setDossierOuvert(c.id)} testId={`cmd-${c.id}`}>
              <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full shrink-0"
                style={{ background: c.en_retard ? "#FEE2E2" : "#DBEAFE",
                         color: c.en_retard ? "#B91C1C" : "#1E3A8A" }}>
                {STATUT_OEM_LABEL[c.statut_oem] || c.statut_oem}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{c.numero} — {c.client}</div>
                <div className="text-[11px] text-[--idc-muted] truncate">
                  {c.date_prevue ? (c.en_retard ? `En retard de ${Math.abs(c.jours_restants)}j` : `Prévue dans ${c.jours_restants}j`) : "Sans date prévue"}
                </div>
              </div>
            </Ligne>
          ))}
        </Widget>
      </div>

      {dossierOuvert && (
        <PanneauAdministratif dossierId={dossierOuvert} onClose={() => setDossierOuvert(null)} />
      )}
    </div>
  );
};
